<template>
	<div class="municipality-search">
		<!-- Manual Search Section -->
		<div class="row mb-3">
			<div :class="enableCsvUploadSearch ? 'col-md-6' : 'col-md-12'">
				<div class="form-group">
					<label for="search">
						Municipality
						<span role="button" :title="'You can search name and state separated with comma, example:black river,wi'">
							ℹ️
						</span>
					</label>
					<input type="text" class="form-control" v-model="search" placeholder="type to search for municipality" />
				</div>
				<div class="search-results-container position-relative">
					<div class="search-results position-absolute bg-white rounded shadow-sm" style="z-index:999">
						<template v-if="!loading">
							<template v-if="municipalities.length > 0">
								<template v-for="municipality in municipalities">
									<div :key="municipality.id" @click="setMunicipality(municipality)" class="search-item ps-2 pe-2 py-1 d-flex">
										<span class="me-2 w-50">{{ municipality.full_name }}</span>
										<span class="me-2 w-25">{{ municipality.county }}</span>
										<span class="w-25 text-end pe-2">{{ municipality.state }}</span>
									</div>
								</template>
							</template>
							<template v-if="municipalities.length === 0 && search.length > 3">
								<span class="me-2 ms-2">Municipality not found!</span>
							</template>
						</template>
						<template v-else>
							<div class="d-flex justify-content-center p-2">
								<div class="spinner-border spinner-border-sm" role="status">
									<span class="sr-only">Loading...</span>
								</div>
							</div>
						</template>
					</div>
				</div>
			</div>

			<!-- CSV Upload Section -->
			<div v-if="enableCsvUploadSearch" class="col-md-6">
				<div class="form-group">
					<label for="csvFile">
						📁 Upload CSV/ID File
					</label>
					<input type="file" ref="csvFile" @change="handleFileUpload" accept=".csv,.xlsx,.xls,.txt" class="form-control" />
					<small class="text-muted">Upload a CSV file with headers or simple ID list (one per line or comma-separated). <strong>Maximum 10,000 IDs per upload.</strong></small>
				</div>
				<div class="mt-2" v-if="csvLoading">
					<button class="btn btn-primary btn-sm" disabled>
						<span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
						Processing...
					</button>
				</div>
				<div class="mt-2" v-if="csvData.length > 0">
					<div class="d-flex align-items-center gap-2 mb-2">
						<span class="fs-8 mb-0 text-nowrap">Select ID Column:</span>
						<select v-model="selectedIdColumn" class="form-select form-select-sm flex-grow-1">
							<option value="">Choose column with municipality IDs</option>
							<option v-for="header in csvHeaders" :key="header" :value="header">{{ header }}</option>
						</select>
					</div>
					<button type="button" @click="processSelectedIds" :disabled="!selectedIdColumn" class="btn btn-primary btn-sm">
						<font-awesome-icon :icon="['fas', 'download']" class="me-1" />
						Fetch {{ csvData.length }} Municipalities
					</button>
				</div>
				<div class="mt-2" v-if="csvProcessedCount > 0">
					<span class="badge bg-success">
						{{ csvProcessedCount }} municipalities added
					</span>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import { mapState } from 'vuex'
import restApi from '../../api/index'
import { handleErrors } from '../../actions/helpers'
export default {
	name: 'MunicipalityAdvancedSearch',
	props: ['excludeClients', 'excludeNoMap', 'enableCsvUploadSearch'],
	data() {
		return {
			loading: false,
			municipalities: [],
			search: '',
			municipalityId: null,
			selectedMunicipality: {
				id: null,
				name: '',
			},
			// Simplified ID upload properties
			csvLoading: false,
			csvProcessedCount: 0,
			csvHeaders: [],
			csvData: [],
			selectedIdColumn: '',
		}
	},
	created() {},
	computed: {
		...mapState(['storageUrl']),
	},
	methods: {
		getMunicipality(searchParams = null) {
			this.loading = true
			let clients = this.excludeClients ? 'yes' : ''
			let noMap = this.excludeNoMap ? 'yes' : ''
			this.params = searchParams || {
				search: this.search,
				clients: clients,
				noMap: noMap,
			}
			
			// Use POST for bulk ID requests, GET for regular search
			const apiCall = searchParams && searchParams.id 
				? restApi.post(`/municipalities/search`, this.params)
				: restApi.get(`/municipalities/search`, { params: this.params })
			
			return apiCall
				.then(({ data }) => {
					this.loading = false
					// Filter out municipalities with disable_campaigns = 1
					const filteredData = data.filter(m => m.disable_campaigns != 1)
					if (!searchParams) {
						this.municipalities = filteredData
					}
					return filteredData
				})
				.catch(error => {
					this.loading = false
					this.$swal.fire({
						icon: 'error',
						title: handleErrors(error, ''),
						timer: 6000,
					})
					throw error
				})
		},
		setMunicipality(municipality) {
			this.selectedMunicipality.id = municipality.id
			this.selectedMunicipality.name = municipality.full_name
			this.municipalities = []
			this.search = ''
			this.$emit('setMunicipality', municipality)
		},
		handleFileUpload(event) {
			const file = event.target.files[0];
			if (!file) return;
			
			this.csvLoading = true;
			this.csvProcessedCount = 0;
			
			const reader = new FileReader();
			reader.onload = (e) => this.processIdFile(e.target.result);
			reader.onerror = () => this.showError('Error reading file');
			reader.readAsText(file);
		},
		processIdFile(text) {
			try {
				const lines = text.split('\n').filter(line => line.trim());
				
				if (lines.length > 1 && lines[0].includes(',')) {
					const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
					this.csvHeaders = headers;
					this.csvData = lines.slice(1).map(line => {
						const values = line.split(',').map(v => v.trim().replace(/"/g, ''));
						return headers.reduce((obj, header, index) => ({ ...obj, [header]: values[index] }), {});
					});
					this.selectedIdColumn = headers.find(h => ['id', 'ID', 'Id', 'municipality_id', 'Municipality_ID', 'MUNICIPALITY_ID'].includes(h)) || '';
					this.csvLoading = false;
					this.resetFileInput();
					this.$swal.fire({ icon: 'success', title: 'CSV uploaded successfully!', text: `${this.csvData.length} rows loaded`, timer: 3000 });
				} else {
					const municipalityIds = (text.includes(',') ? text.split(',') : text.split(/\r?\n/))
						.map(id => id.trim()).filter(id => id);
					if (municipalityIds.length === 0) return this.showError('No valid IDs found in file');
					if (municipalityIds.length > 10000) return this.showError('Too many IDs', 'Maximum 10,000 IDs allowed per upload');
					this.fetchMunicipalitiesFromIds(municipalityIds);
				}
			} catch (error) {
				this.showError('Error processing file', 'Please check your file format and try again.');
			}
		},
		processSelectedIds() {
			if (!this.selectedIdColumn) {
				this.showError('Please select an ID column');
				return;
			}
			const municipalityIds = this.csvData
				.map(row => row[this.selectedIdColumn])
				.filter(id => id && id.trim())
				.map(id => id.trim());
			if (municipalityIds.length === 0) {
				this.showError('No valid IDs found in the selected column');
				return;
			}
			if (municipalityIds.length > 10000) {
				this.showError('Too many IDs', 'Maximum 10,000 IDs allowed per upload');
				return;
			}
			this.csvLoading = true;
			this.fetchMunicipalitiesFromIds(municipalityIds);
		},
		fetchMunicipalitiesFromIds(municipalityIds) {
			const searchParams = {
				id: municipalityIds,
				clients: this.excludeClients ? 'yes' : '',
				noMap: this.excludeNoMap ? 'yes' : '',
				limit: 0
			};
			this.getMunicipality(searchParams)
				.then((municipalities) => {
					municipalities.forEach(municipality => {
						this.$emit('setMunicipality', municipality);
					});
					this.csvProcessedCount = municipalities.length;
					this.csvLoading = false;
					this.resetFileInput();
					const notFoundCount = municipalityIds.length - municipalities.length;
					const message = `Added ${municipalities.length} municipalities` + 
						(notFoundCount > 0 ? `\n${notFoundCount} IDs not found` : '');
					this.$swal.fire({
						icon: municipalities.length > 0 ? 'success' : 'warning',
						title: 'ID Import Complete',
						text: message,
						timer: 4000
					});
				})
				.catch(() => {
					this.csvLoading = false;
					this.resetFileInput();
				});
		},

		showError(title, text = '') {
			this.csvLoading = false;
			this.resetFileInput();
			this.$swal.fire({ icon: 'error', title, text, timer: 4000 });
		},

		resetFileInput() {
			if (this.$refs.csvFile) {
				this.$refs.csvFile.value = '';
			}
		},
	},
	watch: {
		search: function() {
			if (this.search.length >= 2) {
				if (!this.loading) {
					this.getMunicipality()
				}
			} else {
				this.municipalities = []
			}
		},
	},
}
</script>

<style lang="scss">
.search-results-container {
	.search-results {
		width: 100%;
		.search-item {
			cursor: pointer;
			&:hover {
				background-color: whitesmoke;
				border-radius: 0.5rem;
			}
		}
	}
}
</style>
